// Reading a few seconds out of a (possibly several-hundred-MB) WAV without downloading it: fetch the
// header once, then only the byte range the excerpt covers, and decode it here. Works against a
// presigned bucket URL (production) or the app's own file route (local), both of which honour Range.
import { authHeaders } from "../api/client";

export interface WavInfo {
  sampleRate: number;
  channels: number;
  bits: number;
  float: boolean;
  blockAlign: number;
  dataStart: number;
  dataBytes: number;
  duration: number;
}

export interface Excerpt {
  samples: Float32Array;   // mono
  sampleRate: number;
  t0: number;              // seconds from the start of the recording where `samples` begins
}

const infoCache = new Map<string, Promise<WavInfo>>();

async function fetchRange(url: string, start: number, end: number, signal?: AbortSignal): Promise<ArrayBuffer> {
  const headers: Record<string, string> = { Range: `bytes=${start}-${end}` };
  if (url.startsWith("/")) Object.assign(headers, await authHeaders());
  const res = await fetch(url, { headers, signal });
  if (res.status !== 206 && res.status !== 200) throw new Error(`Couldn't read the recording (HTTP ${res.status})`);
  const buf = await res.arrayBuffer();
  return res.status === 200 ? buf.slice(start, end + 1) : buf;   // a server that ignored Range sent it all
}

function tag(v: DataView, o: number) {
  return String.fromCharCode(v.getUint8(o), v.getUint8(o + 1), v.getUint8(o + 2), v.getUint8(o + 3));
}

// A WAV past 4 GB can't give its sizes in 32 bits: RF64 (EBU Tech 3306) and BW64 (ITU-R BS.2088) put the real
// ones in a "ds64" chunk and write 0xFFFFFFFF where they would go. A whole book in one file was RF64.
const WAV_MAGIC = ["RIFF", "RF64", "BW64"];
const SIZE_IN_DS64 = 0xffffffff;

function parseHeader(buf: ArrayBuffer): WavInfo | "more" {
  const v = new DataView(buf);
  if (buf.byteLength < 12 || !WAV_MAGIC.includes(tag(v, 0)) || tag(v, 8) !== "WAVE") {
    throw new Error("The picture and playback need a WAV recording");
  }
  let o = 12;
  let fmt: Omit<WavInfo, "dataStart" | "dataBytes" | "duration"> | null = null;
  let ds64Data: number | null = null;
  while (o + 8 <= buf.byteLength) {
    const id = tag(v, o);
    const size = v.getUint32(o + 4, true);
    if (id === "ds64") {
      if (o + 8 + 16 > buf.byteLength) return "more";
      // after the RIFF size: the data size, 64-bit (exact in a double up to 8 PB)
      ds64Data = v.getUint32(o + 16, true) + v.getUint32(o + 20, true) * 2 ** 32;
    } else if (id === "fmt ") {
      if (o + 8 + 16 > buf.byteLength) return "more";
      let format = v.getUint16(o + 8, true);
      if (format === 0xfffe && o + 8 + 26 <= buf.byteLength) format = v.getUint16(o + 8 + 24, true);   // extensible
      fmt = {
        channels: v.getUint16(o + 10, true), sampleRate: v.getUint32(o + 12, true),
        blockAlign: v.getUint16(o + 20, true), bits: v.getUint16(o + 22, true), float: format === 3,
      };
    } else if (id === "data") {
      if (!fmt) throw new Error("This WAV has no format chunk before its audio");
      const bytes = size === SIZE_IN_DS64 && ds64Data !== null ? ds64Data : size;
      const frames = Math.floor(bytes / fmt.blockAlign);
      return { ...fmt, dataStart: o + 8, dataBytes: bytes, duration: frames / fmt.sampleRate };
    }
    o += 8 + size + (size & 1);
  }
  return "more";
}

export function wavInfo(url: string, cacheKey = url): Promise<WavInfo> {
  let p = infoCache.get(cacheKey);
  if (!p) {
    p = (async () => {
      for (const n of [65536, 1 << 20, 4 << 20]) {         // headers with big metadata chunks need more
        const r = parseHeader(await fetchRange(url, 0, n - 1));
        if (r !== "more") return r;
      }
      throw new Error("Couldn't find the audio in this WAV's header");
    })();
    infoCache.set(cacheKey, p);
    p.catch(() => infoCache.delete(cacheKey));
  }
  return p;
}

export async function wavExcerpt(url: string, t0: number, t1: number, cacheKey = url, signal?: AbortSignal): Promise<Excerpt> {
  const info = await wavInfo(url, cacheKey);
  const total = Math.floor(info.dataBytes / info.blockAlign);
  const f0 = Math.max(0, Math.floor(t0 * info.sampleRate));
  const f1 = Math.min(total, Math.ceil(t1 * info.sampleRate));
  if (f1 <= f0) return { samples: new Float32Array(0), sampleRate: info.sampleRate, t0 };
  const buf = await fetchRange(url, info.dataStart + f0 * info.blockAlign, info.dataStart + f1 * info.blockAlign - 1, signal);
  const v = new DataView(buf);
  const n = Math.floor(buf.byteLength / info.blockAlign);
  const out = new Float32Array(n);
  const bps = info.bits / 8;
  for (let i = 0; i < n; i++) {
    let acc = 0;
    for (let c = 0; c < info.channels; c++) {
      const o = i * info.blockAlign + c * bps;
      let x: number;
      if (info.float && info.bits === 32) x = v.getFloat32(o, true);
      else if (info.bits === 16) x = v.getInt16(o, true) / 32768;
      else if (info.bits === 24) {
        const b = v.getUint8(o) | (v.getUint8(o + 1) << 8) | (v.getInt8(o + 2) << 16);
        x = b / 8388608;
      } else if (info.bits === 32) x = v.getInt32(o, true) / 2147483648;
      else if (info.bits === 8) x = (v.getUint8(o) - 128) / 128;
      else throw new Error(`${info.bits}-bit audio isn't supported here`);
      acc += x;
    }
    out[i] = acc / info.channels;
  }
  return { samples: out, sampleRate: info.sampleRate, t0: f0 / info.sampleRate };
}

// ---- playback -------------------------------------------------------------------------------------
let ctx: AudioContext | null = null;
let current: { src: AudioBufferSourceNode; startedAt: number; offset: number; id: string } | null = null;

export function audioContext(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Play an excerpt from `offset` seconds into it. Stops whatever was playing. `onEnd` fires when it ends. */
export function playExcerpt(ex: Excerpt, id: string, offset = 0, onEnd?: () => void) {
  stopPlayback();
  const ac = audioContext();
  if (!ex.samples.length) return;
  const buf = ac.createBuffer(1, ex.samples.length, ex.sampleRate);
  buf.getChannelData(0).set(ex.samples);
  const src = ac.createBufferSource();
  src.buffer = buf;
  src.connect(ac.destination);
  src.onended = () => {
    if (current?.src === src) current = null;
    onEnd?.();
  };
  src.start(0, offset);
  current = { src, startedAt: ac.currentTime, offset, id };
}

export function stopPlayback() {
  if (current) {
    const c = current;
    current = null;
    try { c.src.stop(); } catch { /* already stopped */ }
  }
}

/** Seconds into the playing excerpt, or null if `id` isn't what's playing. */
export function playbackPosition(id: string): number | null {
  if (!current || current.id !== id || !ctx) return null;
  return current.offset + (ctx.currentTime - current.startedAt);
}
